import { FC, useCallback, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Input, Popconfirm, Row, Switch } from 'antd';
import { TbAccessPoint, TbAlertTriangle, TbCircle, TbCopy, TbMinusVertical, TbPlus, TbTrash } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { workspaceService } from '@/services/database';
import { EmptySection } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useUserStore, useWorkspaceStore } from '@/stores';
import { WorkspaceNetworkAccess, WorkspaceNetworkAccessRule } from '@/types';

import AdvancedSettingsSectionHeader from '../../../components/AdvancedSettingsSectionHeader';

type NetworkAccessRuleInput = Pick<WorkspaceNetworkAccessRule, 'label' | 'value'> &
    Partial<Pick<WorkspaceNetworkAccessRule, 'description' | 'id'>>;

type ApiErrorLike = {
    response?: {
        data?: {
            error?: string;
        };
    };
};

const getApiErrorDescription = (error: unknown, fallback: string) =>
    (error as ApiErrorLike)?.response?.data?.error ?? fallback;

const NetworkAccessSection: FC = (): JSX.Element => {
    const { selectedUser: user } = useUserStore();
    const { selectedWorkspace: workspace } = useWorkspaceStore();
    const { token } = useAntDesignTheme();
    const { hasFeature } = useEntitlements();

    const [enabled, setEnabled] = useState(false);
    const [networkAccess, setNetworkAccess] = useState<WorkspaceNetworkAccess>();
    const [rules, setRules] = useState<WorkspaceNetworkAccessRule[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const canManage = ['Owner', 'Admin'].includes(String(user?.activeMember?.permission));
    const canUseNetworkAccess = hasFeature('networkAccess');
    const workspaceId = workspace?.id;
    const currentIp = networkAccess?.currentIp;
    const disabled = !canUseNetworkAccess || !canManage || loading || saving;
    const hasRules = rules.length > 0;
    const shouldShowPanel = enabled || Boolean(networkAccess?.enabled);
    const lastUpdatedLabel = networkAccess?.updatedAt ? new Date(networkAccess.updatedAt).toLocaleString() : undefined;

    const handleGetNetworkAccess = useCallback(async () => {
        if (!workspaceId) {
            return;
        }

        try {
            setLoading(true);

            const response = await workspaceService.getNetworkAccess(workspaceId);

            setNetworkAccess(response);
            setEnabled(response.enabled);
            setRules(response.rules ?? []);
        } catch (error) {
            captureException(error);
        } finally {
            setLoading(false);
        }
    }, [workspaceId]);

    useEffect(() => {
        handleGetNetworkAccess();
    }, [handleGetNetworkAccess]);

    const updateRule = (id: string, property: 'description' | 'label' | 'value', value: string) => {
        setRules((previous) => previous.map((rule) => (rule.id === id ? { ...rule, [property]: value } : rule)));
    };

    const addRule = (value?: string) => {
        setRules((previous) => [
            ...previous,
            {
                createdAt: new Date().toISOString(),
                description: value ? 'Automatically added from this browser session.' : '',
                id: `draft-${Date.now()}`,
                label: value ? 'Current Network' : 'Approved Network',
                value: value ?? '',
            },
        ]);
    };

    const removeRule = (id: string) => {
        setRules((previous) => previous.filter((rule) => rule.id !== id));
    };

    const handleCopyCurrentIp = async () => {
        if (!currentIp || currentIp === 'Unknown') {
            return;
        }

        await navigator.clipboard.writeText(currentIp);
        toast('Current IP copied', { description: currentIp });
    };

    const handleSave = async () => {
        if (!workspace?.id) {
            return;
        }

        const normalizedRules = rules.reduce<NetworkAccessRuleInput[]>((accumulator, rule) => {
            const value = (rule.value ?? '').trim();

            if (!value) {
                return accumulator;
            }

            accumulator.push({
                description: rule.description?.trim() ?? '',
                id: rule.id?.startsWith('draft-') ? undefined : rule.id,
                label: rule.label?.trim() || value || 'Approved Network',
                value,
            });

            return accumulator;
        }, []);

        if (enabled && normalizedRules.length === 0) {
            toast.error('Add an approved network before enabling network access');

            return;
        }

        try {
            setSaving(true);

            const response = await workspaceService.updateNetworkAccess(workspace.id, {
                enabled,
                rules: normalizedRules,
            });

            setNetworkAccess(response);
            setEnabled(response.enabled);
            setRules(response.rules ?? []);
            toast.success('Network access updated', {
                description: response.enabled
                    ? `${response.rules.length} approved ${response.rules.length === 1 ? 'network' : 'networks'} enforced.`
                    : 'Workspace network restrictions are disabled.',
            });
        } catch (error) {
            captureException(error);
            toast.error('Could not update network access', {
                description: getApiErrorDescription(error, 'Review the approved networks and try again.'),
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <AdvancedSettingsSectionHeader
                description='Restrict workspace access to approved IP addresses or CIDR ranges.'
                icon={<TbAccessPoint size='1rem' />}
                plan='enterprise'
                title='Network Access'
                extra={
                    <Switch
                        disabled={disabled}
                        loading={loading}
                        size='default'
                        style={{ verticalAlign: '-1rem' }}
                        checked={enabled}
                        checkedChildren={<TbMinusVertical style={{ verticalAlign: '-0.1rem' }} />}
                        unCheckedChildren={<TbCircle style={{ verticalAlign: '-0.1rem' }} />}
                        onChange={(checked: boolean) => {
                            setEnabled(checked);
                        }}
                    />
                }
            />
            {shouldShowPanel && (
                <div
                    style={{
                        background: token.colorBgContainer,
                        border: `1px dashed ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                        marginBottom: '1.5rem',
                        padding: '1rem',
                    }}
                >
                    {enabled && (
                        <>
                            <Row gutter={[12, 12]} align='middle' style={{ marginBottom: '1rem' }}>
                                <Col flex='auto'>
                                    <PlatformAlert
                                        showIcon
                                        type='warning'
                                        icon={<TbAlertTriangle style={{ verticalAlign: '-0.125rem' }} />}
                                        message='Network enforcement is active'
                                        description='Only members connecting from an approved network can open protected workspace routes or accept workspace invitations.'
                                    />
                                </Col>
                                <Col flex='none'>
                                    <Button
                                        disabled={!currentIp || currentIp === 'Unknown'}
                                        onClick={handleCopyCurrentIp}
                                        style={{ marginRight: '0.5rem' }}
                                    >
                                        <TbCopy
                                            style={{
                                                color: token.colorTextTertiary,
                                                marginRight: '0.25rem',
                                                verticalAlign: '-0.125rem',
                                            }}
                                        />
                                        {currentIp ?? 'Detecting IP'}
                                    </Button>
                                    <Button
                                        disabled={disabled || !currentIp || currentIp === 'Unknown'}
                                        onClick={() => addRule(currentIp)}
                                        style={{ marginRight: '0.5rem' }}
                                    >
                                        <TbPlus
                                            style={{
                                                color: token.colorTextTertiary,
                                                marginRight: '0.25rem',
                                                verticalAlign: '-0.125rem',
                                            }}
                                        />
                                        Add Current IP
                                    </Button>
                                    <Button disabled={disabled} onClick={() => addRule()}>
                                        <TbPlus
                                            style={{
                                                color: token.colorTextTertiary,
                                                marginRight: '0.25rem',
                                                verticalAlign: '-0.125rem',
                                            }}
                                        />
                                        Add Network
                                    </Button>
                                </Col>
                            </Row>
                            {lastUpdatedLabel && (
                                <small
                                    style={{
                                        color: token.colorTextTertiary,
                                        display: 'block',
                                        marginBottom: '0.75rem',
                                    }}
                                >
                                    Last updated {lastUpdatedLabel}
                                </small>
                            )}
                            {!canUseNetworkAccess && (
                                <PlatformAlert
                                    showIcon
                                    type='warning'
                                    message='Network access controls require Enterprise'
                                    description='Upgrade the workspace plan before enforcing IP allowlisting.'
                                    style={{ marginBottom: '1rem' }}
                                />
                            )}
                            {!canManage && (
                                <PlatformAlert
                                    showIcon
                                    type='info'
                                    message='Owner or Admin permission required'
                                    description='Only workspace owners and admins can change approved network rules.'
                                    style={{ marginBottom: '1rem' }}
                                />
                            )}
                            {hasRules ? (
                                <div>
                                    {rules.map((rule) => (
                                        <Row
                                            key={rule.id}
                                            gutter={[12, 12]}
                                            align='bottom'
                                            style={{
                                                borderTop: `1px solid ${token.colorBorderSecondary}`,
                                                padding: '0.875rem 0',
                                            }}
                                        >
                                            <Col flex='1 1 12rem'>
                                                <small style={{ color: token.colorTextTertiary }}>Label</small>
                                                <Input
                                                    disabled={disabled}
                                                    placeholder='Corporate VPN...'
                                                    value={rule.label}
                                                    onChange={(event) =>
                                                        updateRule(rule.id, 'label', event.target.value)
                                                    }
                                                />
                                            </Col>
                                            <Col flex='1 1 14rem'>
                                                <small style={{ color: token.colorTextTertiary }}>
                                                    IP Address or CIDR
                                                </small>
                                                <Input
                                                    disabled={disabled}
                                                    placeholder='203.0.113.10 or 203.0.113.0/24...'
                                                    value={rule.value}
                                                    onChange={(event) =>
                                                        updateRule(rule.id, 'value', event.target.value)
                                                    }
                                                />
                                            </Col>
                                            <Col flex='2 1 18rem'>
                                                <small style={{ color: token.colorTextTertiary }}>Description</small>
                                                <Input
                                                    disabled={disabled}
                                                    placeholder='Optional Operational Context...'
                                                    value={rule.description}
                                                    onChange={(event) =>
                                                        updateRule(rule.id, 'description', event.target.value)
                                                    }
                                                />
                                            </Col>
                                            <Col flex='none'>
                                                <Popconfirm
                                                    title='Remove approved network?'
                                                    description='This rule will be removed when you save changes.'
                                                    okText='Remove'
                                                    okButtonProps={{ danger: true }}
                                                    onConfirm={() => removeRule(rule.id)}
                                                >
                                                    <Button danger disabled={disabled}>
                                                        <TbTrash style={{ verticalAlign: '-0.125rem' }} />
                                                    </Button>
                                                </Popconfirm>
                                            </Col>
                                        </Row>
                                    ))}
                                </div>
                            ) : (
                                <EmptySection
                                    height={16 * 14}
                                    icon={<TbAccessPoint style={{ color: token.colorTextTertiary }} />}
                                    label='No Approved Networks'
                                    description='Add an exact IP address or CIDR range before enabling network enforcement for this workspace.'
                                    extra={
                                        <Button disabled={disabled} onClick={() => addRule()}>
                                            <TbPlus
                                                style={{
                                                    color: token.colorTextTertiary,
                                                    marginRight: '0.25rem',
                                                    verticalAlign: '-0.125rem',
                                                }}
                                            />
                                            Add Network
                                        </Button>
                                    }
                                />
                            )}
                        </>
                    )}
                    <Row justify='end' style={{ marginTop: enabled ? '1rem' : 0 }}>
                        <Button disabled={disabled} loading={saving} type='primary' onClick={handleSave}>
                            Save
                        </Button>
                    </Row>
                </div>
            )}
        </>
    );
};

export default NetworkAccessSection;
